import { Input, Node, Output } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { booleanDataSocket, categoricalDataSocket, dataSocket, numericDataSocket, propertySocket } from "../socket_types"
import { BooleanTileGrid, CategoricalTileGrid, NumericTileGrid, TileGridJSON, TileGridProps } from "../tile_grid"
import { BaseComponent } from "./base_component"
import { ProjectProperties } from "./index"
import { createXYZ } from "ol/tilegrid"
import { maskFromExtentAndShape } from "../bounding_box"
import { retrieveModelData, retrieveWFSData } from "../model_retrieval"
import { Feature } from "ol"
import { Geometry } from "ol/geom"
import { TypedArray } from "d3"
import { CheckboxSelectControl } from "../controls/checkbox-select"

export interface VectorLayerData {
    name: string
    source: string[]
    key: string
    value?: string[]
    /** Display names for categorical classes. Falls back to `value` when omitted. */
    labels?: string[]
    output: 'BooleanTileGrid' | 'CategoricalTileGrid' | 'NumericTileGrid'
    properties?: TileGridProps
    distributed: boolean
    wms: boolean
    wmsTriggerZoom?: number
    groups?: string[],
    valueAsNaN?: number[],
    cql?: string,
    transform?: (val: number) => number
}

const featuresCache: Map<string, Feature<Geometry>[]> = new Map()

async function buildVectorTileGridFromWMS(layer: VectorLayerData, projectProps: ProjectProperties) : Promise<BooleanTileGrid | CategoricalTileGrid | NumericTileGrid> {

    const [projectExtent, zoom] = [projectProps.extent, projectProps.zoom]
   
    const mask = await maskFromExtentAndShape(projectProps)

    const tileGrid = createXYZ()
    const outputTileRange = tileGrid.getTileRangeForExtentAndZ(projectProps.extent, projectProps.zoom)

    const cacheKey = layer.source.join(',')
    const geotiff = await retrieveModelData(projectExtent, cacheKey, outputTileRange)

    const rasters = await geotiff.readRasters({ bbox: projectExtent, width: outputTileRange.getWidth(), height: outputTileRange.getHeight() })
    const image = await geotiff.getImage()

    const result = new BooleanTileGrid(
        zoom,
        outputTileRange.minX,
        outputTileRange.minY,
        outputTileRange.getWidth(),
        outputTileRange.getHeight()
    )

    for (let i = 0; i < (rasters[3] as TypedArray).length; i++) {

        let x = (outputTileRange.minX + i % image.getWidth())
        let y = (outputTileRange.minY + Math.floor(i / image.getWidth()))

        result.set(x, y, rasters[3][i] === 0 ? false : (mask.get(x, y) === true ? true : false))
    
    }

    return result
    
}

async function buildVectorTileGrid(layer: VectorLayerData, projectProps: ProjectProperties) : Promise<BooleanTileGrid | CategoricalTileGrid | NumericTileGrid> {

    // TODO: Add support for multiple sources
    const typeName = layer.source[0] // only first source currently used
    const cacheKey = [typeName, layer.cql].join('||')
    const features = featuresCache.get(cacheKey) ?? await retrieveWFSData(typeName, projectProps, layer.cql)
    featuresCache.set(cacheKey, features)
    const tileGrid = createXYZ()

    const [projectExtent, zoom, maskMode, maskLayer, maskCQL, maskShape] = [projectProps.extent, projectProps.zoom, projectProps.mask, projectProps.maskLayer, projectProps.maskCQL, projectProps.maskShape]
    const outputTileRange = tileGrid.getTileRangeForExtentAndZ(projectProps.extent, projectProps.zoom)
    const mask = await maskFromExtentAndShape(projectProps)

    let result: BooleanTileGrid | CategoricalTileGrid | NumericTileGrid

    switch(layer.output) {
        case 'BooleanTileGrid':
            result = new BooleanTileGrid(zoom, outputTileRange.minX, outputTileRange.minY, outputTileRange.getWidth(), outputTileRange.getHeight())
            break
        case 'CategoricalTileGrid':
            result = new CategoricalTileGrid(zoom, outputTileRange.minX, outputTileRange.minY, outputTileRange.getWidth(), outputTileRange.getHeight())
            const displayLabels = layer.labels ?? layer.value
            result.setLabels(new Map(displayLabels?.map((v, i) => [i+1, v])))
            break
        case 'NumericTileGrid':
            result = new NumericTileGrid(zoom, outputTileRange.minX, outputTileRange.minY, outputTileRange.getWidth(), outputTileRange.getHeight())
            if(layer.properties) {
                result.properties = layer.properties
            }
            break
    }

    for (const feature of features) 
    {
        
        let value: string | number | boolean | undefined = undefined;
        switch(layer.output) {
            case 'BooleanTileGrid':
                value = feature.get(layer.key) as string
                let v = layer.value ? layer.value.includes(value) : true;
                if(v) {
                    result.polygonToTileGrid(feature, v, mask)
                }
                break
            case 'CategoricalTileGrid':
                value = feature.get(layer.key) as string
                let index = layer.value ? layer.value.indexOf(value)+1 : 0;
                if(!isNaN(index) && index !== Infinity && index !== -Infinity) {
                    result.polygonToTileGrid(feature, index, mask)
                }
                break
            case 'NumericTileGrid':
                value = feature.get(layer.key) as string
                let numValue = parseFloat(value)
                if (layer.valueAsNaN && Array.isArray(layer.valueAsNaN)) {
                    numValue = layer.valueAsNaN.includes(numValue) ? NaN : numValue
                }
                if (layer.transform) {
                    numValue = layer.transform(numValue)
                }
                if(!isNaN(numValue) && numValue !== Infinity && numValue !== -Infinity) {
                    result.polygonToTileGrid(feature, numValue, mask)
                }
                break
        }
    }
    result.name = layer.name
    return result
}

export class VectorComponent extends BaseComponent {
    layers: VectorLayerData[]
    projectProps: ProjectProperties
    tileGridsCache: Map<string, BooleanTileGrid | CategoricalTileGrid | NumericTileGrid>
    groups: string[]

    constructor(name: string, layers: VectorLayerData[], projectProps: ProjectProperties, category?: string, groups?: string[]) {
        super(name)
        this.category = category ?? "Inputs"
        this.layers = layers
        this.projectProps = projectProps
        this.tileGridsCache = new Map()
        this.groups = groups ?? []
    }

    loadLayers(node: Node) {

        const group_vals: string[][] = []

        this.groups.forEach((group, i) => {            
            const values = this.layers.map(layer => layer.groups?.[i]).filter((v): v is string => typeof v === "string");
            const uniqueValues = Array.from(new Set(values));
            const selectedIndices = node.data[`group${i}`] as string[] | undefined;
            const selectedValues = selectedIndices 
                ? selectedIndices.map(idx => uniqueValues[Number(idx)]).filter((v): v is string => v !== undefined)
                : [];
            group_vals.push(selectedValues)
        })

        Array.from(node.outputs.entries()).forEach(([key, output]) => {
            output.connections.forEach(connection => {
                connection.remove()
            })
            node.removeOutput(output)
        })

        if(group_vals.length > 0) {
            // If any group has no selection, filter everything (no outputs)
            if (group_vals.some(vals => vals.length === 0)) {
                node.update()
                return
            }
            this.layers.filter(layer => layer.groups?.every((group, i) => group_vals[i].includes(group))).forEach((layer, i) => {
                const socket = layer.output === 'NumericTileGrid' ? numericDataSocket : (layer.output === 'BooleanTileGrid' ? booleanDataSocket : categoricalDataSocket)
                node.addOutput(new Output(i.toString(), layer.name, socket))
            })
        }else{
            this.layers.forEach((layer, i) => {
                const socket = layer.output === 'NumericTileGrid' ? numericDataSocket : (layer.output === 'BooleanTileGrid' ? booleanDataSocket : categoricalDataSocket)
                node.addOutput(new Output(i.toString(), layer.name, socket))
            })
        }

        node.update()
    }

    async builder(node: Node) {

        for (let key in node.data) {
            // Backwards compatibility for old node data
            if (key.startsWith('group')) {
                if (!Array.isArray(node.data[key])) {
                    node.data[key] = [node.data[key]]
                }
            }
        }

        this.groups.forEach((group, i) => {
            const values = this.layers.map(layer => layer.groups?.[i]).filter((v): v is string => typeof v === "string");
            const uniqueValues = Array.from(new Set(values));
            if (node.data[`group${i}`] === undefined) {
                node.data[`group${i}`] = i == 0 ? [i] : Array.from({ length: uniqueValues.length }, (_, i) => 0 + i)
            }
            node.addControl(new CheckboxSelectControl(
                this.editor, 
                `group${i}`, 
                () => uniqueValues.map((value, j) => ({name: value, id: j})), 
                () => this.loadLayers(node), 
                group
            ))
            if (node.data[`group${i}`] === undefined) {
                node.data[`group${i}`] = []
            }
        })

        this.loadLayers(node)
    }

    async worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs, ...args: unknown[]) {

        const editorNode = this.editor?.nodes.find(n => n.id === node.id)
        if (editorNode === undefined) { return }

        const group_vals: string[][] = []
        this.groups.forEach((group, i) => {            
            const values = this.layers.map(layer => layer.groups?.[i]).filter((v): v is string => typeof v === "string");
            const uniqueValues = Array.from(new Set(values));
            const selectedIndices = node.data[`group${i}`] as string[] | undefined;
            const selectedValues = selectedIndices 
                ? selectedIndices.map(idx => uniqueValues[Number(idx)]).filter((v): v is string => v !== undefined)
                : [];
            group_vals.push(selectedValues)
        })

        // If any group has no selection, filter everything
        if (group_vals.length > 0 && group_vals.some(vals => vals.length === 0)) {
            return
        }

        const p = this.layers.filter(layer => (group_vals.length === 0 || layer.groups?.every((group, i) => group_vals[i].includes(group)))).map(async (layer, i) => {
            if(node.outputs[i.toString()].connections.length > 0) {
                const tileGrid = this.tileGridsCache.get(layer.name) ?? (layer.wms && this.projectProps.zoom <= (layer.wmsTriggerZoom ?? 0) ? await buildVectorTileGridFromWMS(layer, this.projectProps) : await buildVectorTileGrid(layer, this.projectProps))
                this.tileGridsCache.set(layer.name, tileGrid)
                outputs[i.toString()] = tileGrid
            }
        })

        await Promise.all(p)

    }
}